import React, { useEffect } from "react";
import { Link } from "react-router-dom";
import HubTopBar from "../components/aa/HubTopBar";
import Footer from "../components/aa/Footer";
import { Button } from "../components/ui/button";
import {
  ArrowRight,
  Sparkles,
  MessageCircle,
  Wallet,
  Globe2,
  Megaphone,
  Zap,
  Lock,
} from "lucide-react";
import { useLang } from "../i18n/LanguageContext";
import { HUB_TRANSLATIONS, HUB_IMAGES } from "../data/hub";
import { WHATSAPP_URL } from "../config";

const iconFor = (i) => [Wallet, Globe2, Megaphone, Zap][i] || Sparkles;

export default function PartnersHub() {
  const { lang } = useLang();
  const t = HUB_TRANSLATIONS[lang] || HUB_TRANSLATIONS.en;
  const openWa = () => window.open(WHATSAPP_URL, "_blank", "noopener,noreferrer");

  useEffect(() => {
    const io = new IntersectionObserver(
      (entries) => {
        entries.forEach((e) => {
          if (e.isIntersecting) {
            e.target.classList.add("in");
            io.unobserve(e.target);
          }
        });
      },
      { threshold: 0.12 }
    );
    document.querySelectorAll(".reveal").forEach((el) => io.observe(el));
    return () => io.disconnect();
  }, [lang]);

  return (
    <div className="min-h-screen bg-[var(--aa-cream)] text-[var(--aa-ink)] font-body">
      <HubTopBar />

      {/* HERO */}
      <section className="relative overflow-hidden bg-[var(--aa-wine)] text-[var(--aa-cream)]">
        <div className="absolute inset-0">
          <img src={HUB_IMAGES.hero} alt="" className="w-full h-full object-cover opacity-35 scale-105" />
          <div className="absolute inset-0 bg-gradient-to-b from-[var(--aa-wine)]/85 via-[var(--aa-wine)]/70 to-[var(--aa-wine)]"></div>
        </div>
        <div className="pointer-events-none absolute -top-40 -right-40 w-[520px] h-[520px] rounded-full border border-white/10"></div>
        <div className="pointer-events-none absolute -bottom-56 -left-40 w-[640px] h-[640px] rounded-full border border-white/5"></div>

        <div className="relative mx-auto max-w-6xl px-6 md:px-8 pt-20 md:pt-28 pb-24 md:pb-32 text-center">
          <div className="reveal in inline-flex items-center gap-2 px-3 py-1.5 rounded-full border border-white/25 bg-white/5 text-[11px] tracking-[0.22em] uppercase">
            <Sparkles className="w-3.5 h-3.5" />
            {t.hero.eyebrow}
          </div>
          <h1 className="reveal font-display font-light text-[44px] leading-[1.05] sm:text-6xl md:text-[76px] md:leading-[1.02] mt-7 max-w-4xl mx-auto">
            {t.hero.h1a}
            <br />
            <span className="italic font-normal text-[var(--aa-cream)]/95">{t.hero.h1b}</span>
          </h1>
          <p className="reveal max-w-2xl mx-auto mt-7 text-[15px] md:text-[17px] leading-relaxed text-[var(--aa-cream)]/80">
            {t.hero.p}
          </p>
          <div className="reveal mt-10 inline-flex flex-col items-center gap-5">
            <Button
              onClick={openWa}
              className="group rounded-full bg-[var(--aa-cream)] hover:bg-white text-[var(--aa-wine)] px-7 py-6 text-[15px] font-semibold shadow-[0_10px_40px_-10px_rgba(0,0,0,0.4)]"
            >
              <MessageCircle className="w-4 h-4 mr-2" />
              {t.hero.cta}
            </Button>
            <div className="text-xs text-[var(--aa-cream)]/60">{t.hero.tagline}</div>
          </div>
        </div>
      </section>

      {/* SEGMENTS */}
      <section id="partners" className="relative pt-24 pb-20 md:py-32 bg-[var(--aa-cream)]">
        <div className="mx-auto max-w-6xl px-6 md:px-8">
          <div className="reveal max-w-3xl">
            <div className="text-[11px] tracking-[0.24em] uppercase text-[var(--aa-wine)] font-semibold mb-4">
              {t.segments.eyebrow}
            </div>
            <h2 className="font-display font-light text-4xl md:text-6xl leading-[1.05] text-[var(--aa-ink)]">
              {t.segments.h2a} <br className="hidden md:block" />
              <span className="italic">{t.segments.h2b}</span>
            </h2>
          </div>

          <div className="mt-14 grid md:grid-cols-3 gap-5 md:gap-6">
            {t.segments.cards.map((c) => {
              const img = HUB_IMAGES[c.key];
              const CardTag = c.available ? Link : "div";
              const cardProps = c.available ? { to: c.href } : {};
              return (
                <CardTag
                  key={c.key}
                  {...cardProps}
                  className={`reveal lift group relative rounded-3xl overflow-hidden bg-white border border-[var(--aa-line)] block ${
                    c.available ? "cursor-pointer" : "cursor-default"
                  }`}
                >
                  <div className="relative aspect-[4/3] overflow-hidden">
                    <img
                      src={img}
                      alt={c.tag}
                      className={`w-full h-full object-cover transition-transform duration-700 ${
                        c.available ? "group-hover:scale-105" : "opacity-70"
                      }`}
                    />
                    <div className="absolute inset-0 bg-gradient-to-t from-black/50 via-black/10 to-transparent"></div>
                    <div className="absolute top-4 left-4 inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[var(--aa-cream)] text-[var(--aa-wine)] text-[10.5px] uppercase tracking-[0.18em] font-semibold">
                      {c.tag}
                    </div>
                    {!c.available && (
                      <div className="absolute top-4 right-4 inline-flex items-center gap-1 px-2.5 py-1 rounded-full bg-white/15 backdrop-blur border border-white/25 text-white text-[10px] uppercase tracking-[0.18em]">
                        <Lock className="w-3 h-3" />
                        Soon
                      </div>
                    )}
                  </div>
                  <div className="p-6 md:p-7">
                    <h3 className="font-display text-2xl md:text-[26px] leading-tight text-[var(--aa-ink)]">
                      {c.title}
                    </h3>
                    <p className="mt-3 text-[14px] text-[var(--aa-ink-soft)] leading-relaxed">
                      {c.body}
                    </p>
                    <div
                      className={`mt-5 inline-flex items-center gap-1.5 text-[13px] font-semibold ${
                        c.available
                          ? "text-[var(--aa-wine)] group-hover:gap-2.5 transition-all"
                          : "text-[var(--aa-ink-soft)]"
                      }`}
                    >
                      {c.cta}
                      {c.available && <ArrowRight className="w-4 h-4" />}
                    </div>
                  </div>
                </CardTag>
              );
            })}
          </div>
        </div>
      </section>

      {/* WHY */}
      <section id="why" className="relative py-24 md:py-32 bg-[var(--aa-cream-warm)]">
        <div className="mx-auto max-w-6xl px-6 md:px-8">
          <div className="reveal max-w-3xl">
            <div className="text-[11px] tracking-[0.24em] uppercase text-[var(--aa-wine)] font-semibold mb-4">
              {t.why.eyebrow}
            </div>
            <h2 className="font-display font-light text-4xl md:text-6xl leading-[1.05] text-[var(--aa-ink)]">
              {t.why.h2a} <br className="hidden md:block" />
              <span className="italic">{t.why.h2b}</span>
            </h2>
          </div>
          <div className="mt-14 grid md:grid-cols-2 gap-5 md:gap-6">
            {t.why.items.map((it, i) => {
              const Ico = iconFor(i);
              return (
                <div
                  key={i}
                  className="reveal lift group relative rounded-3xl bg-white border border-[var(--aa-line)] p-7 md:p-9 overflow-hidden"
                >
                  <div className="absolute -top-8 -right-8 w-40 h-40 rounded-full bg-[var(--aa-cream-warm)] opacity-0 group-hover:opacity-100 transition-opacity duration-500"></div>
                  <div className="relative flex items-center gap-3">
                    <span className="inline-flex items-center justify-center w-11 h-11 rounded-2xl bg-[var(--aa-wine)] text-[var(--aa-cream)]">
                      <Ico className="w-5 h-5" />
                    </span>
                    <span className="text-xs tracking-[0.18em] uppercase text-[var(--aa-wine)]/70">
                      0{i + 1}
                    </span>
                  </div>
                  <h3 className="relative font-display text-2xl md:text-[26px] mt-6 text-[var(--aa-ink)] leading-tight">
                    {it.title}
                  </h3>
                  <p className="relative mt-3 text-[14.5px] text-[var(--aa-ink-soft)] leading-relaxed">
                    {it.body}
                  </p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section id="contact" className="relative py-24 md:py-32 bg-[var(--aa-cream)]">
        <div className="mx-auto max-w-4xl px-6 md:px-8">
          <div className="reveal rounded-[32px] bg-[var(--aa-wine)] text-[var(--aa-cream)] p-10 md:p-16 relative overflow-hidden text-center">
            <div className="absolute -top-24 -right-24 w-64 h-64 rounded-full bg-white/5"></div>
            <div className="absolute -bottom-24 -left-24 w-72 h-72 rounded-full bg-white/5"></div>
            <div className="relative">
              <div className="text-[11px] tracking-[0.24em] uppercase text-[var(--aa-cream)]/70 font-semibold mb-4">
                {t.cta.eyebrow}
              </div>
              <h3 className="font-display font-light text-4xl md:text-6xl leading-[1.02]">
                {t.cta.h3a} <span className="italic">{t.cta.h3b}</span>
              </h3>
              <p className="mt-6 text-[15px] md:text-lg text-[var(--aa-cream)]/80 max-w-xl mx-auto">
                {t.cta.p}
              </p>
              <div className="mt-8 flex flex-col items-center gap-3">
                <Button
                  onClick={openWa}
                  className="rounded-full bg-[var(--aa-cream)] hover:bg-white text-[var(--aa-wine)] px-7 py-6 font-semibold group"
                >
                  <MessageCircle className="w-4 h-4 mr-2" />
                  {t.cta.btn}
                </Button>
                <div className="font-mono text-sm text-[var(--aa-cream)]/70">{t.cta.number}</div>
              </div>
            </div>
          </div>
        </div>
      </section>

      <Footer />
    </div>
  );
}
